/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ScheduleService} from "../../providers/schedule-service";
import {ScheduleItem} from "../../models/schedule/ScheduleItem";
import {CompEvent} from "../../models/CompEvent";
import {Subscription} from "rxjs";
import {EntryScheduleItem} from "../../models/schedule/EntryScheduleItem";
import {FollowingService} from "../../providers/following-service";
import {EntityFollowing} from "../../models/following/EntityFollowing";
import {Observable} from "rxjs/Observable";
import {ActionSheetButton, ActionSheetController, AlertController, Toggle} from "ionic-angular";
import {UserInterfacingService} from "../../providers/user-interfacing-service/user-interfacing-service";
import {ScheduleFilterOptions} from "../../shared/PageRefs";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {Studio} from "../../models/Studio";


@Component({
  selector: 'day-schedule',
  templateUrl: 'day-schedule.html'
})
export class DayScheduleComponent implements OnInit, OnDestroy {

  // region Subscriptions

  private _entrySubscription: Subscription;
  private _followingSubscription: Subscription;
  private _filterSubscription: Subscription;
  private _medalSubscription: Subscription;

  // endregion

  // region UI Bindings

  entries: Array<ScheduleItem> = [];
  entriesFollowing: {} = {};

  private _inFilterEntries$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  inFilterEntries$ = this._inFilterEntries$.asObservable();

  private _showStaticItems$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showStaticItems$: Observable<boolean> = this._showStaticItems$.asObservable();

  // endregion

  // region Awards

  private _entryMedals: BehaviorSubject<any> = new BehaviorSubject<any>({});
  entryMedals$ = this._entryMedals.asObservable();

  // endregion

  // region Inputs/Outputs

  @Input()
  date: string;

  @Input()
  event: CompEvent;

  @Output()
  entrySelected: EventEmitter<EntryScheduleItem> = new EventEmitter();

  @Input()
  isAutoScrollChecked: boolean = true;

  @Output()
  dateButtonClick: EventEmitter<null> = new EventEmitter<null>();

  @Output()
  autoToggleChanged: EventEmitter<boolean> = new EventEmitter();

  // endregion


  constructor(private actionSheetCtrl: ActionSheetController,
              private alertCtrl: AlertController,
              private uiService: UserInterfacingService,
              private scheduleService: ScheduleService,
              private followingService: FollowingService) {

  }

  // region Lifecycle Events

  ngOnInit(): void {
    this.initSubscriptions();
  }


  ngOnDestroy(): void {
    console.debug('Destroy');
    if (this._entrySubscription) {
      this._entrySubscription.unsubscribe();
    }
    if (this._followingSubscription) {
      this._followingSubscription.unsubscribe();
    }
    if (this._filterSubscription) {
      this._filterSubscription.unsubscribe();
    }
    if (this._medalSubscription) {
      this._medalSubscription.unsubscribe();
    }
  }

  // endregion

  // region UI Interactions

  selectEntry(entry: EntryScheduleItem) {
    this.entrySelected.emit(entry);
  }

  showFollowOptions(event: any, sliding: any, item: ScheduleItem) {
    event.stopPropagation();

    let actionSheet = this.actionSheetCtrl.create({
      title: 'Follow Options',
      buttons: this.getFollowOptionButtons(item as EntryScheduleItem)
    });

    actionSheet.onWillDismiss(() => {
      sliding.close();
    });

    actionSheet.present();
  }

  vote(event: any, sliding: any, item: ScheduleItem) {
    event.stopPropagation();
    sliding.close();
    // TODO
  }

  // endregion

  // region Private Helper Methods

  private initSubscriptions() {
    const dayEntries$ = this.scheduleService.entries
      .map(entries => entries.filter(entry => entry.date == this.date));

    this._entrySubscription = dayEntries$.subscribe(entries => {
      this.entries = entries;
      // console.log(entries);
    }, err => {
      console.error(err);
    });

    const routines$ : Observable<EntryScheduleItem[]> = dayEntries$
      .map(entries => entries.filter(entry => entry.routineType == 1) as EntryScheduleItem[]);

    const followedRoutines$: Observable<EntryState[]> = Observable.combineLatest(
      routines$, this.followingService.entitiesFollowing$, (entries: any, following: any) => {
        return entries
          .map((entry: EntryScheduleItem) => {

            let studioFollowed : boolean = following
              .filter((followed: EntityFollowing) =>
                followed.entityType == EntityFollowing.STUDIOS &&
                followed.entityId == entry.studio.id
              )
              .length > 0;

            let dancerFollowed : boolean = following
              .filter((followed: EntityFollowing) =>
                followed.entityType == EntityFollowing.DANCERS &&
                followed.entityId == entry.studio.id &&
                entry.dancerList.filter(dancer => dancer.toUpperCase() == followed.name.toUpperCase()).length > 0
              )
              .length > 0;

            return {
              entryId: entry.id,
              dancerFollowed: dancerFollowed,
              studioFollowed: studioFollowed
            };
          });
      });

    this._followingSubscription = followedRoutines$
      .map((results: EntryState[]) =>
        results.filter(result => !!result && (result.dancerFollowed || result.studioFollowed)))
      .subscribe(results => {

        let following = {};
        results.map(result => {
          following[result.entryId] = result;
        });

        this.entriesFollowing = following;
      });

    const filters$: Observable<ScheduleFilterOptions> = this.scheduleService.selectedFilters$;

    this._filterSubscription = Observable.combineLatest(
      routines$, filters$, followedRoutines$, this.scheduleService.entryMedals$, (routines, filters, followed, medals) => {

        // console.log('filters');
        this._showStaticItems$.next(filters.isShowBreaks);
        // console.log(filters);
        // console.log(medals);

        return  routines.filter(routine => {
          console.log('filtering...');
          console.log(routine);

          const medalIndex = medals.findIndex(m => m.entryId == routine.id);
          let isMedalMatch: boolean = false;

          if (medalIndex > -1) {
            const medalId = medals[medalIndex].awardId;
            isMedalMatch = !!filters.medals[medalId];
          }

          return (filters.isIncludeStudios || followed.findIndex(f => f.entryId == routine.id && f.studioFollowed) > -1) &&
            (filters.isIncludeDancers || followed.findIndex(f => f.entryId == routine.id && f.dancerFollowed) > -1) &&
            DayScheduleComponent.isTextMatch(filters.searchText, routine) &&
            (!filters.isEnabled || (
              (Object.keys(filters.ages).length < 1 || filters.ages[routine.ageCategory.id]) &&
              (Object.keys(filters.medals).length < 1 || isMedalMatch) &&
              (Object.keys(filters.categories).length < 1 || filters.categories[routine.category.id]) &&
              (Object.keys(filters.classifications).length < 1|| filters.classifications[routine.classification.id]) &&
              (Object.keys(filters.types).length < 1 || filters.types[routine.entryType.id]))
            )
        }).map(routine => routine.id);
      }
    )
      // .distinctUntilChanged(null, x => x[1])
      .subscribe(filteredIds => {
        // console.log(`There are changes to filtered entries. Now showing ${filteredIds.length}`);

        let filtered: any = {};
        filteredIds.forEach(id => {
          filtered[id] = true;
        });

        this._inFilterEntries$.next(filtered);
      });

    this. _medalSubscription = Observable
      .combineLatest(
        this._inFilterEntries$, this.scheduleService.entryMedals$, (filtered, medals) => {
          return medals.filter(m => !!filtered[m.entryId]);
        })
      // .distinctUntilChanged(null, x => x[1])
      .subscribe(medals => {
        let values = {};
        medals.forEach(m => {
          values[m.entryId] = m;
        });
        // console.log('received medals');
        // console.log(values);
        this._entryMedals.next(values);
      });
  }

  private static isTextMatch(text: string, item: EntryScheduleItem) : boolean {
    if (!text || text.length == 0 ) {
       return true;
    }

    const search = text.toLowerCase();
    const entryNumber = item.entryNumber ? item.entryNumber.toLowerCase() : '';

    return item.title.toLowerCase().indexOf(search) > -1 ||
      item.dancers.toLowerCase().indexOf(search) > -1 ||
      (!entryNumber || entryNumber.startsWith(search));
  }

  private getFollowOptionButtons(item: EntryScheduleItem) : ActionSheetButton[] {

    let buttons: ActionSheetButton[] = [];

    // dancers
    const dancerButton: ActionSheetButton = {
      text: 'Follow/Unfollow Dancers',
      icon: 'ios-body',
      handler: () => {
        console.log('Follow dancers clicked');
        this.displayDancerList(item);
      }
    };

    buttons.push(dancerButton);

    // studio
    let isStudioFollowed = this.entriesFollowing[item.id] && this.entriesFollowing[item.id].studioFollowed;
    const studioButton: ActionSheetButton = {
      text: isStudioFollowed ? 'Unfollow Studio' : 'Follow Studio',
      icon: 'md-home',
      handler: () => {
        console.log('Do studio action');
        this.followUnfollowStudio(item.studio, !isStudioFollowed);
      }
    };
    if (isStudioFollowed) {
      studioButton.role = 'destructive';
    }
    buttons.push(studioButton);

    // cancel
    buttons.push({
      text: 'Cancel',
      role: 'cancel',
      handler: () => {
        console.log('Cancel clicked');
      }
    });

    return buttons;
  }

  private displayDancerList(entry: EntryScheduleItem) {
    this.followingService.entitiesFollowing$
      .map(entities => entities.filter(entity =>
        entity.entityType == EntityFollowing.DANCERS &&
        entity.entityId == entry.studio.id
      ))
      .map(dancers => dancers.map(dancer => dancer.name))
      .first()
      .subscribe(names => {
        let alert = this.alertCtrl.create({
          title: 'Follow/Unfollow Dancers',
          subTitle: 'Checked dancers will be followed, Unchecked dancers will be unfollowed',
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: data => {
                console.log('Cancel clicked');
              }
            },
            {
              text: 'Done',
              handler: data => {
                console.log('Done clicked');
                console.log(data);
                this.updateFollowedDancers(entry, data, names);
              }
            }
          ],
          inputs: entry.dancerList.map(dancer => {
            return {
              type: 'checkbox',
              label: dancer,
              value: dancer,
              checked: names.filter(name => name.toUpperCase() == dancer.toUpperCase()).length > 0
            }
          })
        });
        alert.present();
      });
  }

  private updateFollowedDancers(entry: EntryScheduleItem, selectedDancers: string[], originalDancers: string[]) {

    const dancersToAdd: string[] = selectedDancers.filter(selected => originalDancers.indexOf(selected) < 0);

    // remove any dancer that no longer should be followed
    let dancersToRemove: string[] = [];
    originalDancers.forEach(followed => {
      if (entry.dancerList.findIndex(d => d.toUpperCase() == followed.toUpperCase()) >= 0 &&
        selectedDancers.findIndex(d => d.toUpperCase() == followed.toUpperCase()) < 0) {

        dancersToRemove.push(followed);
      }
    });

    this.followingService.updateDancersFollowed(entry.studio.id, dancersToAdd, dancersToRemove)
      .subscribe(result => {
        this.uiService.showToastMessage('Dancer(s) successfully updated');
      }, err => {
        this.uiService.showDismissibleError(err);
      });
  }

  private followUnfollowStudio(studio: Studio, shouldFollow: boolean) {
    if (!shouldFollow) {
      this.uiService.showYesNoPrompt('Unfollow Studio', 'Are you sure you want to stop following this studio?',
        () => {
          console.log('No clicked');
        },
        () => {
          console.log('Yes clicked');
          this.doStudioFollowAction(studio, shouldFollow);
        });
    } else {
      this.doStudioFollowAction(studio, shouldFollow);
    }
  }

  private doStudioFollowAction(studio: Studio, shouldFollow: boolean) {
    this.followingService.updateStudioFollowing(studio, shouldFollow)
      .subscribe(
        (result) => {
          this.uiService.showToastMessage('Studio successfully updated');
        }, err => {
          this.uiService.showDismissibleError(err);
        }
      );
  }

  private trackByEntryId(index, item) {
    return item.id;
  }
  // endregion

  onDateButtonClicked() {
    this.dateButtonClick.next();
  }

  onAutoChanged($event: Toggle) {
    this.autoToggleChanged.next($event.checked);
  }
}

export interface EntryState {
  entryId: string,
  dancerFollowed: boolean,
  studioFollowed: boolean
}



// WEBPACK FOOTER //
// ./src/components/day-schedule/day-schedule.ts